// Modules
import React from "react";
import BaseBlockContent from "@sanity/block-content-to-react";
import PropTypes from "prop-types";
import cx from "classnames";

// Assets
import Check from "../../../assets/svg/check";

// Components
import Button from "../button";
import CustomGatsbyImage from "../gatsbyImage";
import HorizontalLine from "../horizontalLine";
import Testimonials from "../testimonials";
import TextImage from "../textImage";
import VideoEmbed from "../videoEmbed";
import FormattedLink from "../link";
import Columns from "../columns";

// Helpers
import generateRandomId from "../../helpers/generateRandomId";

// Styles
import * as styles from "./style.module.scss";

// Constants
import COLORS, { textColors } from "../../constants/colors";
import { builderType } from "../../constants/constants";

const getMarkedList = ({ children, style = "normal", markDefs, _key }) => {
  const listData = (data, i) => {
    const isBold = !!data.marks?.find((child) => child === "strong");
    const isItalic = !!data.marks?.find((child) => child === "em");
    const stringColor = data.marks?.find((child) => {
      for (let value of Object.keys(textColors)) {
        if (value === child) {
          return child;
        }
      }
    });

    if (!data.marks?.length) {
      return data.text;
    } else {
      const marksData = markDefs?.find((md) => {
        for (let value of data.marks) {
          if (md._key === value) {
            return md;
          }
        }
      });

      switch (marksData?._type) {
        case "id":
          return (
            <span key={i.toString()} id={marksData.id?.current}>
              {data.text}
            </span>
          );
        case "link":
          const linkClassName = `${styles.richTextLink} ${
            styles[stringColor ?? "pink"]
          }`;

          return isBold ? (
            <b className={styles.normalBold} key={i.toString()}>
              <FormattedLink
                className={linkClassName}
                {...marksData}
                url={marksData?.link}
              >
                {data.text}
              </FormattedLink>
            </b>
          ) : (
            <FormattedLink
              key={i.toString()}
              className={linkClassName}
              {...marksData}
              url={marksData?.link}
            >
              {data.text}
            </FormattedLink>
          );
        default:
          return (
            <span
              key={i.toString()}
              className={cx({ [styles[stringColor]]: stringColor })}
            >
              {isBold ? (
                <b className={styles.normalBold}>{data.text}</b>
              ) : isItalic ? (
                <i>{data.text}</i>
              ) : (
                data.text
              )}
            </span>
          );
      }
    }
  };

  return (
    <li key={_key}>
      {style === "normal" ? (
        <p>{children.map((child, i) => listData(child, i))}</p>
      ) : style === "h1" || style === "h2" ? (
        <h2>{children.map((child, i) => listData(child, i))}</h2>
      ) : (
        <h3>{children.map((child, i) => listData(child, i))}</h3>
      )}
    </li>
  );
};

const getItems = (value) => {
  const { marks, style, text } = value;

  const isBold = !!marks?.find((child) => child === "strong") ?? false;
  const stringColor = marks?.find((child) => {
    for (let value of Object.keys(textColors)) {
      if (value === child) {
        return child;
      }
    }
  });
  const styledClassName = styles[stringColor ?? "black"];

  return (
    <li key={String(generateRandomId())}>
      {style === "normal" ? (
        isBold ? (
          <p className={styledClassName}>
            <b className={styles.normalBold}>{text || ""}</b>
          </p>
        ) : (
          <p className={styledClassName}>{text || ""}</p>
        )
      ) : style === "h1" || style === "h2" ? (
        <h2 className={styledClassName}>{text || ""}</h2>
      ) : (
        <h3 className={styledClassName}>{text || ""}</h3>
      )}
    </li>
  );
};

const getList = (data) => {
  const { children, style = "normal" } = data;
  const firstLevelMarks = children.find((v) => v.marks?.length);

  return (
    <>
      {firstLevelMarks
        ? getMarkedList(data)
        : children.map((firstLevelChild, index) => {
            if (firstLevelChild._type !== "list") {
              return getItems({ ...firstLevelChild, style });
            } else {
              const ListTag =
                firstLevelChild.children[0].listItem === "bullet" ? "ul" : "ol";

              return (
                <ListTag key={String(index)}>
                  {firstLevelChild.children.map((child) => {
                    const { children, style } = child;
                    const secondLevelMarks = children.find(
                      (v) => v.marks?.length
                    );

                    return secondLevelMarks
                      ? getMarkedList(child)
                      : children.map((child) => getItems({ ...child, style }));
                  })}
                </ListTag>
              );
            }
          })}
    </>
  );
};

const BlockContent = ({ blocks, customClassName }) => {
  const serializers = {
    types: {
      simpleText: (props) => {
        const data = props?.node || props;
        const style = data?.style || "normal";
        if (/^h\d/.test(style) && !props.node.listItem) {
          const elementId = generateRandomId();
          const actualStyle = style === "h1" || style === "h2" ? "h2" : "h3";
          const className = cx(styles[actualStyle]);

          const options = { id: `${elementId}`, className };
          return React.createElement(style, options, props.children);
        }

        if (props?.node?.listItem === "bullet") {
          if (data?.markDefs.length > 0) {
            return getMarkedList({ ...data, style });
          }
          return getList(data);
        } else if (props?.node?.listItem === "number") {
          if (data?.markDefs.length > 0) {
            return getMarkedList({ ...data, style });
          }
          return getList(data);
        }

        return BaseBlockContent.defaultSerializers.types.block(props);
      },
      customImage: (props) => {
        const data = props?.node || props;

        return (
          <>
            <CustomGatsbyImage
              alt={data?.alt || generateRandomId()}
              img={data?.image?.asset}
              imgClassName={cx({
                [styles[`reachCustomImg${data.imgSize}`]]: data?.imgSize,
                [styles[`img${data?.positionImg || "Center"}`]]: true,
              })}
            />
            {data?.title && <p className={styles.imgTitle}>{data.title}</p>}
          </>
        );
      },
      buttonLink: (props) =>
        props.node?.btnPosition === "full" ||
        props.node?.btnPosition === undefined ? (
          <Button
            classNameBtn={customClassName}
            link={props.node.url}
            {...props.node}
          >
            {props.node.label}
          </Button>
        ) : (
          <div
            style={{ display: "flex", justifyContent: props.node.btnPosition }}
          >
            <Button
              classNameBtn={customClassName}
              link={props.node.url}
              {...props.node}
            >
              {props.node.label}
            </Button>
          </div>
        ),
      columns: (props) => (
        <Columns key={props._key} {...props.node} type={builderType.columns} />
      ),
      horizontalLine: () => <HorizontalLine />,
      richTextTestimonial: (props) => {
        return (
          <Testimonials
            author={props.node.author}
            bgColor={props.node.bgColor}
            ctDirection={props.node.ctDirection}
            logo={props.node.logo}
            quote={props.node.quote}
            role={props.node.role}
            type={props.node._type}
          />
        );
      },
      textImage: (props) => {
        return (
          <TextImage
            className={customClassName}
            imageData={props.node.imageData}
            imageAlignment={props.node.imageAlignment}
            richText={props.node.richText}
          />
        );
      },
      videoEmbed: (props) => {
        return (
          <VideoEmbed
            aspect={props.node.aspect}
            titleSubtitle={props.node.titleSubtitle}
            url={props.node.url}
            className={customClassName}
          />
        );
      },
    },
    marks: {
      check: ({ children }) => {
        return (
          <span>
            {" "}
            <Check fill={COLORS.pink} /> {children}
          </span>
        );
      },
      darkgreen: ({ children }) => (
        <span className={styles.darkgreen}>{children}</span>
      ),
      orange: ({ children }) => (
        <span className={styles.orange}>{children}</span>
      ),
      pink: ({ children }) => <span className={styles.pink}>{children}</span>,
      skyblue: ({ children }) => (
        <span className={styles.skyblue}>{children}</span>
      ),
      strong: ({ children }) => <b className={styles.normalBold}>{children}</b>,
      link: ({ mark, children }) => {
        return (
          <FormattedLink className={styles.link} {...mark} url={mark.link}>
            {children}
          </FormattedLink>
        );
      },
      id: ({ mark, children }) => <span id={mark.id?.current}>{children}</span>,
    },
  };
  return (
    <BaseBlockContent
      className={styles.textStyle}
      blocks={blocks}
      serializers={serializers}
    />
  );
};

BlockContent.propTypes = {
  blocks: PropTypes.oneOfType([
    PropTypes.shape({
      children: PropTypes.array,
      node: PropTypes.object,
    }),
    PropTypes.array,
  ]),
  customClassName: PropTypes.string,
};

BlockContent.defaultProps = {
  customClassName: "",
};

export default BlockContent;
